// @flow
import * as React from 'react';
import styled, { css } from 'styled-components';
import Box from 'components/Box';
import RadioButton from 'components/RadioButton';
import LargeRadioButton from 'components/LargeRadioButton';
import SquareRadioButton from 'components/SquareRadioButton';
import { colors, breakpoints } from '../theme';

const { useMemo } = React;

const RadioContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  ${props =>
    props.columns
      ? css`
          margin: 0 -6px;
        `
      : css`
          flex-direction: column;
        `};
  justify-content: flex-start;
  border-radius: 4px;
  @media (min-width: ${breakpoints[2]}) {
    flex-direction: row;
  }
  ${props =>
    props.hasError &&
    css`
      border: solid 1px ${colors.scarlet};
      box-shadow: 0 0 0 1px ${colors.scarlet};
      padding-right: 6px;
      padding-left: 6px;
      margin: 0 -${props.columns ? 12 : 6}px;
    `};
`;

const RadioButtons = ({
  id,
  name,
  value,
  buttons,
  large,
  alignTop,
  columns,
  hasError,
  onChange,
  disabled,
  canDeselect,
  hideRadioButtons,
  square,
  ...props
}: {
  id?: string,
  name?: string,
  value: ?(string | boolean | number),
  buttons: Array<{
    label: React.Node,
    value: string | boolean | number,
    disabled?: boolean
  }>,
  large?: boolean,
  alignTop?: boolean,
  columns?: number | Array<number>,
  hasError?: boolean,
  onChange: (string | boolean | number | null) => void,
  disabled?: boolean,
  canDeselect?: boolean,
  hideRadioButtons?: boolean,
  square?: boolean,
  'data-qa-id': string
}) => {
  const RadioButtonComponent = useMemo(
    () => (large ? LargeRadioButton : square ? SquareRadioButton : RadioButton),
    [large, square]
  );
  const colCount =
    columns instanceof Array ? columns.reduce((sum, i) => sum + i, 0) : columns;
  return (
    <RadioContainer
      columns={colCount || square}
      hasError={hasError}
      data-qa-id={props['data-qa-id']}
    >
      {buttons.map(
        (
          { label: inputLabel, value: inputValue, disabled: inputDisabled },
          index
        ) => (
          <Box
            key={inputValue.toString()}
            pr={!columns && index !== buttons.length - 1 ? '50px' : 0}
            p={(!!columns || square) && '6px'}
            width={
              colCount && [
                1,
                1,
                (columns instanceof Array && columns[index]
                  ? columns[index]
                  : 1) / colCount
              ]
            }
          >
            <RadioButtonComponent
              name={name}
              alignTop={large ? alignTop : undefined}
              label={inputLabel}
              id={
                index === 0
                  ? id || props['data-qa-id']
                  : `${id || props['data-qa-id']}-${inputValue.toString()}`
              }
              data-qa-id={`${props['data-qa-id']}-${inputValue.toString()}`}
              onChange={onChange}
              onClick={() => {
                if (canDeselect && value === inputValue) {
                  onChange(null);
                }
              }}
              value={inputValue}
              checked={value === inputValue}
              disabled={!!(disabled || inputDisabled)}
              hideRadioButtons={hideRadioButtons}
            />
          </Box>
        )
      )}
    </RadioContainer>
  );
};

export default RadioButtons;
